/**
 * InvestigationDrawer — 460px persistent drawer for an open investigation.
 *
 * cycle-109 sprint-205 S3.5 full rewrite. Renders the 9-section structure
 * from PRD FR-7.1..7.9 + SDD §6.2 with a state-gated action footer per
 * FR-7.9. Mounts the existing `AeonBriefSlot` (cycle-081) inside FR-7.4.
 *
 * Drawer is pure-presentational: caller (InvestigationDrawerContainer)
 * owns the data hooks (`useInvestigation`, `useEvidence`, `useClaims`,
 * `useCounterSignals`, `useProjectToEnvironment`) and routes derived
 * props down.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { AeonBriefSlot } from './AeonBriefSlot';
import {
  EvidenceChip,
  evidenceKindForStatus,
} from './modules/EvidenceGridModule';
import type {
  ClaimGraphSummary,
  EvidenceItem,
  InvestigationSummary,
} from '../../types/investigation';
import type { AeonBrief } from '../../hooks/useIntelligenceSynthesis';

// ── Derived state ──────────────────────────────────────────────────

export type InvestigationDrawerState = 'SHADOW' | 'ACTIVE' | 'CERTIFIED';

/**
 * Drawer-level state derivation (SDD §6.3). CERTIFIED if the investigation
 * has been promoted to Environment (projected_to_environment_at populated
 * OR status === 'certified'); ACTIVE if status indicates a live readout;
 * SHADOW otherwise.
 */
export function deriveInvestigationDrawerState(
  inv: Pick<InvestigationSummary, 'status' | 'projected_to_environment_at'>,
): InvestigationDrawerState {
  if (inv.projected_to_environment_at || inv.status === 'certified') return 'CERTIFIED';
  if (inv.status === 'active' || inv.status === 'promoted' || inv.status === 'admitted') {
    return 'ACTIVE';
  }
  return 'SHADOW';
}

export type FooterStateKind =
  | 'SHADOW_COUNTER'
  | 'SHADOW_LOW_READINESS'
  | 'SHADOW_READY'
  | 'ACTIVE'
  | 'CERTIFIED';

/**
 * Action-footer state machine (FR-7.9 / AC-3.5.f). SHADOW + counter > 0 →
 * blocked; SHADOW + readiness < 80 → in-progress; SHADOW + readiness ≥ 80
 * → ready to promote; ACTIVE → already live; CERTIFIED → anchored.
 */
export function deriveFooterState(args: {
  state: InvestigationDrawerState;
  readinessPercent: number;
  counterSignalCount: number;
}): FooterStateKind {
  if (args.state === 'CERTIFIED') return 'CERTIFIED';
  if (args.state === 'ACTIVE') return 'ACTIVE';
  if (args.counterSignalCount > 0) return 'SHADOW_COUNTER';
  if (args.readinessPercent < 80) return 'SHADOW_LOW_READINESS';
  return 'SHADOW_READY';
}

/**
 * Readiness bar fill colour per AC-3.5.b: mint ≥80, amber ≥60, rose <60.
 */
export function readinessBarColor(percent: number): string {
  if (percent >= 80) return 'var(--sig-mint)';
  if (percent >= 60) return 'var(--sig-amber)';
  return 'var(--sig-rose)';
}

/**
 * Readiness percent formula per AC-3.5.b:
 *   (corroborated / max(total_inferred, 1)) × 100
 * where `corroborated` = supporting chips and `total_inferred` = all chips.
 * Returns an integer in [0, 100].
 */
export function computeReadinessPercent(args: {
  corroborated: number;
  totalInferred: number;
}): number {
  const denom = Math.max(args.totalInferred, 1);
  return Math.min(100, Math.round((args.corroborated / denom) * 100));
}

// ── Props ──────────────────────────────────────────────────────────

export interface TriggerChainRow {
  id: string;
  title?: string;
  severity?: string;
  region?: string;
}

export interface InvestigationDrawerProps {
  inv: InvestigationSummary;
  /** AC-3.5.b: parent computes via `computeReadinessPercent`. */
  readinessPercent: number;
  /** AC-3.5.c: null → render `—`. */
  confidencePercent: number | null;
  /** AC-3.5.d gating + footer rendering. */
  counterSignalCount: number;
  /** FR-7.6 trigger chain rows. Empty/absent → placeholder. */
  triggeringSignals?: readonly TriggerChainRow[];
  onFocusTrigger?: (signalId: string) => void;
  /** FR-7.4 — null/undefined hides the AeonBriefSlot's render output. */
  aeonBrief?: AeonBrief | null;
  /** FR-7.7 evidence chips. */
  evidenceItems?: readonly EvidenceItem[];
  /** FR-7.8 claims summary. */
  claims?: ClaimGraphSummary | null;
  /** Optional environment-anchor id surfaced in the CERTIFIED banner. */
  envAnchorId?: string | null;
  onClose: () => void;
  /** AC-3.5.e — caller writes localStorage + closes the drawer. */
  onDismiss24h?: () => void;
  /** AC-3.5.g — fires on the ANCHORED · ENV button click. */
  onProjectToEnvironment?: () => void;
  /** In-flight indicator for the ANCHORED · ENV button. */
  projectInFlight?: boolean;
  /** Inline status surfaced under the footer (e.g. JWT affordance). */
  actionError?: string | null;
  className?: string;
}

// ── Component ──────────────────────────────────────────────────────

export function InvestigationDrawer({
  inv,
  readinessPercent,
  confidencePercent,
  counterSignalCount,
  triggeringSignals,
  onFocusTrigger,
  aeonBrief = null,
  evidenceItems,
  claims = null,
  envAnchorId = null,
  onClose,
  onDismiss24h,
  onProjectToEnvironment,
  projectInFlight = false,
  actionError,
  className,
}: InvestigationDrawerProps) {
  const state = deriveInvestigationDrawerState(inv);
  const footer = deriveFooterState({ state, readinessPercent, counterSignalCount });
  const showCounterBanner = counterSignalCount > 0 && readinessPercent < 80;
  const showCertifiedBanner = state === 'CERTIFIED';

  return (
    <aside
      role="dialog"
      aria-modal="false"
      aria-label={`Investigation ${inv.id}`}
      data-testid="investigation-drawer"
      data-state={state}
      className={cn(
        'absolute right-4 top-4 bottom-4 w-[460px] flex flex-col z-[20]',
        'bg-[var(--e-bg-card)] border border-[var(--e-border-secondary)] rounded-md shadow-lg',
        className,
      )}
      style={{ borderLeft: `3px solid ${stateAccent(state)}` }}
    >
      {/* FR-7.1 Header */}
      <header className="px-4 py-3 border-b border-[var(--e-border-secondary)]/60">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--e-text-muted)] flex items-center gap-1.5">
            <span aria-hidden="true" style={{ color: stateAccent(state) }}>◆</span>
            READOUT · {shortId(inv.id)}
          </span>
          <div className="flex items-center gap-2">
            <StatePill state={state} />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close investigation drawer"
              className="text-[14px] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] w-6 h-6 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        </div>

        {/* FR-7.2 Promotion brief — title + [dismiss · 24h] chip */}
        <div className="mt-2 flex items-start justify-between gap-3">
          <h2
            className="text-[18px] leading-snug text-[var(--e-text-primary)] m-0"
            style={{ fontFamily: 'var(--font-display, var(--e-font-sans))' }}
          >
            {investigationTitle(inv)}
          </h2>
          {onDismiss24h && (
            <button
              type="button"
              onClick={onDismiss24h}
              data-testid="investigation-drawer-dismiss-24h"
              className="shrink-0 text-[9px] font-mono uppercase tracking-wider px-2 py-1 rounded border border-[var(--e-border-secondary)] text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]"
            >
              [dismiss · 24h]
            </button>
          )}
        </div>
      </header>

      {/* FR-7.3 Status banner (conditional) */}
      {showCounterBanner && (
        <Banner tone="rose" testId="investigation-drawer-counter-banner">
          ▲ HOLD — COUNTER-SIGNAL · {counterSignalCount} counter-signal
          {counterSignalCount === 1 ? '' : 's'} pending review
        </Banner>
      )}
      {showCertifiedBanner && (
        <Banner tone="iris" testId="investigation-drawer-certified-banner">
          ● CERTIFIED · Promoted to Environment. Anchor active.
          {envAnchorId && <span className="ml-1.5 text-[var(--e-text-muted)]">({envAnchorId})</span>}
        </Banner>
      )}

      {/* Scrollable body — FR-7.4..7.8 */}
      <div className="flex-1 overflow-y-auto">
        {/* FR-7.4 Finding (AEON brief) */}
        <Section title="Finding">
          <div
            data-testid="investigation-drawer-aeon-slot"
            style={{ borderLeft: '2px solid var(--sig-iris)' }}
            className="bg-[var(--e-bg-inset,var(--e-bg-hover))] rounded-r"
          >
            {aeonBrief ? (
              <AeonBriefSlot brief={aeonBrief} variant="inline" />
            ) : (
              <EmptyNote>No Aeon brief yet — synthesis pending.</EmptyNote>
            )}
          </div>
        </Section>

        {/* FR-7.5 Promotion gates */}
        <Section title="Promotion gates">
          <div className="flex flex-col gap-3 px-3 py-2">
            <PromotionBar
              label="Readiness"
              percent={readinessPercent}
              color={readinessBarColor(readinessPercent)}
              testId="investigation-drawer-readiness-bar"
            />
            <PromotionBar
              label="Confidence"
              percent={confidencePercent}
              color={confidencePercent === null ? 'var(--e-text-muted)' : readinessBarColor(confidencePercent)}
              testId="investigation-drawer-confidence-bar"
            />
          </div>
        </Section>

        {/* FR-7.6 Trigger chain */}
        <Section title="Trigger chain">
          <TriggerChain
            rows={triggeringSignals ?? []}
            onFocus={onFocusTrigger}
          />
        </Section>

        {/* FR-7.7 Evidence grid */}
        <Section title="Evidence">
          <EvidenceChipGrid items={evidenceItems ?? []} />
        </Section>

        {/* FR-7.8 Claims row */}
        <Section title="Claims">
          <ClaimsLine claims={claims ?? null} />
        </Section>
      </div>

      {/* FR-7.9 Action footer (state-gated) */}
      <footer className="border-t border-[var(--e-border-secondary)]/60 px-4 py-3 flex flex-col gap-2">
        <ActionFooter
          state={footer}
          counterCount={counterSignalCount}
          readinessPercent={readinessPercent}
          projectInFlight={projectInFlight}
          onProjectToEnvironment={onProjectToEnvironment}
        />
        {actionError && (
          <div
            role="status"
            data-testid="investigation-drawer-action-error"
            className="text-[10.5px] font-mono leading-snug px-2.5 py-1.5 rounded border border-[var(--e-border-secondary)] text-[var(--sig-amber)]"
            style={{ borderLeft: '2px solid var(--sig-amber)' }}
          >
            {actionError}
          </div>
        )}
      </footer>
    </aside>
  );
}

// ── Sub-components (file-local) ────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <div className="px-4 pt-3 pb-1 text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]">
        {title}
      </div>
      <div className="px-4 pb-3">{children}</div>
    </section>
  );
}

function Banner({
  tone,
  testId,
  children,
}: {
  tone: 'rose' | 'iris';
  testId?: string;
  children: React.ReactNode;
}) {
  const color = tone === 'rose' ? 'var(--sig-rose)' : 'var(--sig-iris)';
  return (
    <div
      data-testid={testId}
      className="px-4 py-2 text-[12px] leading-snug text-[var(--e-text-primary)] border-b border-[var(--e-border-secondary)]/60"
      style={{
        background: `color-mix(in oklch, ${color} 12%, transparent)`,
        borderLeft: `2px solid ${color}`,
      }}
    >
      {children}
    </div>
  );
}

function PromotionBar({
  label,
  percent,
  color,
  testId,
}: {
  label: string;
  percent: number | null;
  color: string;
  testId?: string;
}) {
  const safePercent = percent ?? 0;
  const display = percent === null ? '—' : `${Math.round(percent)}%`;
  return (
    <div data-testid={testId} data-percent={percent ?? 'null'}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--e-text-muted)]">
          {label}
        </span>
        <span className="text-[10px] font-mono tabular-nums text-[var(--e-text-primary)]">
          {display}
        </span>
      </div>
      <div
        className="h-1.5 w-[140px] rounded-full bg-[var(--e-bg-inset,var(--e-bg-hover))] overflow-hidden"
      >
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(100, Math.max(0, safePercent))}%`,
            background: color,
          }}
        />
      </div>
    </div>
  );
}

function TriggerChain({
  rows,
  onFocus,
}: {
  rows: readonly TriggerChainRow[];
  onFocus?: (signalId: string) => void;
}) {
  if (rows.length === 0) {
    return <EmptyNote>(no triggering signals recorded)</EmptyNote>;
  }
  return (
    <ol className="list-none m-0 p-0 flex flex-col gap-1.5">
      {rows.map((row, i) => (
        <li key={row.id}>
          <button
            type="button"
            onClick={() => onFocus?.(row.id)}
            data-testid={`investigation-drawer-trigger-${row.id}`}
            className="w-full text-left flex items-center gap-2 px-2.5 py-1.5 rounded border border-[var(--e-border-secondary)]/60 hover:bg-[var(--e-bg-hover)]"
          >
            <span className="text-[9px] font-mono tabular-nums text-[var(--e-text-muted)] w-5 shrink-0">
              {i + 1}.
            </span>
            <span
              className="w-1.5 h-1.5 rounded-full shrink-0"
              style={{ background: severityColor(row.severity) }}
            />
            <span className="font-mono text-[10px] text-[var(--e-text-muted)] shrink-0">{row.id}</span>
            {row.title && (
              <span className="text-[12px] text-[var(--e-text-primary)] truncate">{row.title}</span>
            )}
            {row.region && (
              <span className="ml-auto text-[9px] font-mono text-[var(--e-text-muted)] shrink-0">
                {row.region}
              </span>
            )}
          </button>
        </li>
      ))}
    </ol>
  );
}

function EvidenceChipGrid({ items }: { items: readonly EvidenceItem[] }) {
  if (items.length === 0) {
    return <EmptyNote>No evidence items collected yet.</EmptyNote>;
  }
  return (
    <div className="grid grid-cols-3 gap-1.5" data-testid="investigation-drawer-evidence-grid">
      {items.map((item, i) => {
        const status = (item as { epistemic_status?: string }).epistemic_status as
          | Parameters<typeof evidenceKindForStatus>[0]
          | undefined;
        const kind = evidenceKindForStatus(status);
        const id = (item as { evidence_id?: string }).evidence_id ?? `E-${i + 1}`;
        return (
          <EvidenceChip key={id} kind={kind} testId={`investigation-drawer-evidence-${i + 1}`}>
            E-{String(i + 1).padStart(2, '0')}
          </EvidenceChip>
        );
      })}
    </div>
  );
}

function ClaimsLine({ claims }: { claims: ClaimGraphSummary | null }) {
  if (!claims) {
    return <EmptyNote>No claims composed yet.</EmptyNote>;
  }
  const total = claims.claims.length;
  const composed = claims.status_summary?.composed ?? 0;
  const unresolved = total - composed;
  return (
    <div
      data-testid="investigation-drawer-claims-line"
      className="text-[12px] font-mono tabular-nums text-[var(--e-text-primary)] px-2.5 py-1.5 rounded border border-[var(--e-border-secondary)]/60"
    >
      <span>{total} claim{total === 1 ? '' : 's'}</span>
      <span className="text-[var(--e-text-muted)]"> · </span>
      <span>{composed} composed</span>
      <span className="text-[var(--e-text-muted)]"> · </span>
      <span>{unresolved} unresolved</span>
    </div>
  );
}

function ActionFooter({
  state,
  counterCount,
  readinessPercent,
  projectInFlight,
  onProjectToEnvironment,
}: {
  state: FooterStateKind;
  counterCount: number;
  readinessPercent: number;
  projectInFlight: boolean;
  onProjectToEnvironment?: () => void;
}) {
  const baseBtnClass =
    'text-[11px] font-mono uppercase tracking-wider px-3 py-2 rounded border w-full';

  switch (state) {
    case 'SHADOW_COUNTER':
      return (
        <div data-testid="investigation-drawer-footer" data-footer-state="SHADOW_COUNTER">
          <button
            type="button"
            disabled
            className={cn(baseBtnClass, 'border-[var(--sig-rose)] bg-[var(--sig-rose)]/10 text-[var(--sig-rose)] cursor-not-allowed')}
          >
            PROMOTION BLOCKED
          </button>
          <div className="mt-1 text-[10px] font-mono text-[var(--e-text-muted)]">
            resolve {counterCount} counter-signal{counterCount === 1 ? '' : 's'} · readiness ≥ 80% required
          </div>
        </div>
      );
    case 'SHADOW_LOW_READINESS':
      return (
        <div data-testid="investigation-drawer-footer" data-footer-state="SHADOW_LOW_READINESS">
          <button
            type="button"
            disabled
            className={cn(baseBtnClass, 'border-[var(--e-border-secondary)] text-[var(--e-text-muted)] cursor-not-allowed')}
          >
            — IN PROGRESS —
          </button>
          <div className="mt-1 text-[10px] font-mono text-[var(--e-text-muted)]">
            readiness ≥ 80% required ({Math.round(readinessPercent)}% now)
          </div>
        </div>
      );
    case 'SHADOW_READY':
      return (
        <div data-testid="investigation-drawer-footer" data-footer-state="SHADOW_READY">
          <button
            type="button"
            className={cn(baseBtnClass, 'border-[var(--sig-mint)] bg-[var(--sig-mint)] text-[var(--e-bg-deep,#000)]')}
            data-testid="investigation-drawer-promote-to-active"
          >
            → ACTIVE
          </button>
        </div>
      );
    case 'ACTIVE':
      return (
        <div data-testid="investigation-drawer-footer" data-footer-state="ACTIVE" className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onProjectToEnvironment}
            disabled={projectInFlight || !onProjectToEnvironment}
            aria-label="Export investigation to environment"
            data-testid="investigation-drawer-project-to-environment"
            className={cn(
              baseBtnClass,
              'border-[var(--sig-mint)] bg-[var(--sig-mint)] text-[var(--e-bg-deep,#000)]',
              'disabled:opacity-60 disabled:cursor-wait',
            )}
          >
            {projectInFlight ? 'Promoting…' : '→ Environment'}
          </button>
          <button
            type="button"
            disabled
            aria-label="Export to market coming soon"
            className={cn(baseBtnClass, 'border-[var(--e-border-secondary)] text-[var(--e-text-muted)] cursor-not-allowed')}
          >
            → Market soon
          </button>
        </div>
      );
    case 'CERTIFIED':
      return (
        <div data-testid="investigation-drawer-footer" data-footer-state="CERTIFIED" className="flex flex-col gap-2">
          <button
            type="button"
            data-testid="investigation-drawer-anchored-env"
            className={cn(baseBtnClass, 'border-[var(--sig-iris)] bg-[var(--sig-iris)]/15 text-[var(--sig-iris)]')}
          >
            ✓ ANCHORED · ENV
          </button>
          <button
            type="button"
            disabled
            aria-label="Open position coming soon"
            className={cn(baseBtnClass, 'border-[var(--e-border-secondary)] text-[var(--e-text-muted)] cursor-not-allowed')}
          >
            OPEN POSITION
          </button>
        </div>
      );
  }
}

function StatePill({ state }: { state: InvestigationDrawerState }) {
  const color = stateAccent(state);
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border"
      style={{ color, borderColor: color }}
      data-testid="investigation-drawer-state-pill"
    >
      {state}
    </span>
  );
}

function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] italic text-[var(--e-text-muted)] px-2.5 py-2 rounded border border-dashed border-[var(--e-border-secondary)]/40">
      {children}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────

function stateAccent(state: InvestigationDrawerState): string {
  switch (state) {
    case 'SHADOW':
      return 'var(--sig-ember)';
    case 'ACTIVE':
      return 'var(--sig-mint)';
    case 'CERTIFIED':
      return 'var(--sig-iris)';
  }
}

function severityColor(severity?: string): string {
  switch (severity) {
    case 'critical': return 'var(--sig-rose)';
    case 'high': return 'var(--sig-ember)';
    case 'moderate': return 'var(--sig-amber)';
    case 'low': return 'var(--sig-mint)';
    default: return 'var(--e-text-muted)';
  }
}

function shortId(id: string): string {
  return id.length > 10 ? id.slice(0, 10) : id;
}

function investigationTitle(inv: InvestigationSummary): string {
  if (inv.template_name) return inv.template_name;
  if (inv.inquiry_class) return inv.inquiry_class.replace(/_/g, ' ');
  return inv.id;
}

// Hooks not used in this presentational component — `useState` import kept for
// future expansion (e.g. collapsible sections) without churning the API.
void useState;
